import React from 'react';
import { StyleSheet, View, Image, Text, TouchableOpacity } from 'react-native';
import { useHistory } from 'react-router';
import { Auth } from 'aws-amplify';

import { privacyPolicyLink, termsOfServiceLink } from '../../Version';
import { localeTexts } from '../../Locales';
import color from '../../style/color';
import { font } from '../../style/text';
import { useWindowSize } from '../../style/utils';
import AuthPageLayout from './AuthPageLayout';
import {
    clearPendingEmailVerification,
    getPendingEmailVerification,
    setPendingEmailVerification,
} from '../../lib/common/pendingEmailVerification';
import { usePrevious } from '../../lib/common/utilHooks';
import Button from '../../components/common/Button';
import TextField from '../../components/common/TextField';
import HighlightedText from '../../components/common/HighlightedText';
import PasswordRequirements, { usePasswordRequirementsChecks } from '../../components/auth/PasswordRequirements';

const { v1: uuidv1 } = require('uuid');

const logo = { uri: '/assets/images/logos/logo-with-title.png' };
// Thin, sharp tick rather than the heavier legacy `check-mark.svg`
const checkMarkIcon = { uri: '/assets/images/icons/check-mark-white-thin.svg' };
// Joko 3D asset (the "Hourglass" from the brand library, also used in the mobile app) — the pending
// state of this screen is a wait. Black + green accent on a light background is on-brand.
const hourglassIllustration = { uri: '/assets/images/illustrations/hourglass.png' };
const eyeIcon = { uri: '/assets/images/icons/eye.svg' };
const eyeSlashIcon = { uri: '/assets/images/icons/eye-slash.svg' };

const texts = localeTexts.auth.signUp;

export default function SignUpPage() {
    const history = useHistory();
    const [firstName, setFirstName] = React.useState<string>('');
    const [lastName, setLastName] = React.useState<string>('');
    const [email, setEmail] = React.useState<string>('');
    const [password, setPassword] = React.useState<string>('');
    const [passwordConfirmation, setPasswordConfirmation] = React.useState<string>('');
    const previousFirstName = usePrevious(firstName);
    const previousLastName = usePrevious(lastName);
    const previousEmail = usePrevious(email);
    const previousPassword = usePrevious(password);
    const passwordRequirementsChecks = usePasswordRequirementsChecks(password);
    const [consentedToTermsOfService, setConsentedToTermsOfService] = React.useState<boolean>(false);
    const [signUp, isLoading, isEmailVerificationNeeded, error, clearError] = useSignUp();
    const canSignUp = React.useMemo(
        () =>
            firstName.length > 0 &&
            lastName.length > 0 &&
            isEmailValid(email) &&
            consentedToTermsOfService &&
            passwordConfirmation === password &&
            passwordRequirementsChecks.all,
        [firstName, lastName, email, consentedToTermsOfService, password, passwordConfirmation, passwordRequirementsChecks]
    );
    const onPress = () => {
        if (canSignUp) signUp(firstName, lastName, email, password);
    };
    React.useEffect(() => {
        if (
            firstName.length !== previousFirstName?.length ||
            lastName.length !== previousLastName?.length ||
            email.length !== previousEmail?.length ||
            password.length !== previousPassword?.length
        )
            clearError();
    }, [
        firstName,
        lastName,
        email,
        password,
        previousFirstName,
        previousLastName,
        previousEmail,
        previousPassword,
        clearError,
    ]);
    if (!isEmailVerificationNeeded)
        return (
            <AuthPageLayout>
                <Text style={styles.textTitle}>{texts.title}</Text>
                <View style={styles.containerField}>
                    <FirstNameInput {...{ firstName, setFirstName, isLoading }} />
                </View>
                <View style={styles.containerField}>
                    <LastNameInput {...{ lastName, setLastName, isLoading }} />
                </View>
                <View style={styles.containerField}>
                    <EmailInput {...{ email, setEmail, isLoading }} />
                </View>
                <View style={styles.containerField}>
                    <PasswordInput {...{ password, setPassword, isLoading }} />
                    {password.length > 0 ? (
                        <View style={styles.containerPasswordRequirements}>
                            <PasswordRequirements {...{ passwordRequirementsChecks }} />
                        </View>
                    ) : null}
                </View>
                <View style={styles.containerField}>
                    <PasswordInput
                        password={passwordConfirmation}
                        setPassword={setPasswordConfirmation}
                        placeholder={texts.passwordConfirmationPlaceholder}
                        hasError={passwordConfirmation.length > 0 && password !== passwordConfirmation}
                        errorMessage={texts.passwordConfirmationError}
                        {...{ isLoading }}
                    />
                </View>
                <TermsOfServiceConsentCheckBox
                    isChecked={consentedToTermsOfService}
                    setIsChecked={setConsentedToTermsOfService}
                />
                {error ? <ErrorMessage {...{ error }} /> : null}
                <Button
                    {...{ onPress, isLoading }}
                    useBlackColor={true}
                    textStyle={styles.textSignUpButton}
                    style={styles.containerSignUpButton}
                    height={40}
                    disabled={!canSignUp}>
                    {texts.signUpButton}
                </Button>
                {/* One line, like the matching link on the sign-in page */}
                <View style={styles.containerSignIn}>
                    <Text style={styles.textSignIn}>{texts.signIn.text + ' '}</Text>
                    <TouchableOpacity onPress={() => history.push('/auth/sign-in')}>
                        <Text style={styles.textSignInButton}>{texts.signIn.button}</Text>
                    </TouchableOpacity>
                </View>
            </AuthPageLayout>
        );
    else return <EmailVerificationMessage email={email || getPendingEmailVerification() || ''} {...{ password }} />;
}

enum SignUpError {
    Other = 'Other',
}

function useSignUp(): [
    (firstName: string, lastName: string, email: string, password: string) => Promise<void>,
    boolean,
    boolean,
    SignUpError | undefined,
    () => void
] {
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    // Restored from local storage so refreshing the "verify your email" screen keeps the user
    // there instead of dropping them back on an empty form (QA CUX-800).
    const [isEmailVerificationNeeded, setIsEmailVerificationNeeded] = React.useState<boolean>(
        () => getPendingEmailVerification() !== undefined
    );
    const [error, setError] = React.useState<SignUpError | undefined>();
    const signUp = async (firstName: string, lastName: string, email: string, password: string): Promise<void> => {
        if (isEmailValid(email)) {
            setIsLoading(true);
            try {
                const userId: string = uuidv1();
                await Auth.signUp({
                    username: userId,
                    password,
                    attributes: { email: email.toLowerCase(), family_name: lastName, given_name: firstName },
                });
                setPendingEmailVerification(email);
                setIsEmailVerificationNeeded(true);
            } catch (error) {
                console.log(error);
                setError(SignUpError.Other);
            }
            setIsLoading(false);
        }
    };
    const clearError = () => setError(undefined);
    return [signUp, isLoading, isEmailVerificationNeeded, error, clearError];
}

export function isEmailValid(email: string): boolean {
    const regex =
        /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
    return regex.test(email.toLowerCase());
}

function FirstNameInput({
    firstName,
    setFirstName,
    isLoading,
}: {
    firstName: string;
    setFirstName: (firstName: string) => void;
    isLoading: boolean;
}) {
    return (
        <TextField
            placeholder={texts.firstNamePlaceholder}
            value={firstName}
            blurOnSubmit={false}
            autoCorrect={false}
            autoCapitalize={'words'}
            keyboardType={'default'}
            returnKeyType="next"
            autoFocus={true}
            editable={!isLoading}
            onChangeText={setFirstName}
        />
    );
}

function LastNameInput({
    lastName,
    setLastName,
    isLoading,
}: {
    lastName: string;
    setLastName: (lastName: string) => void;
    isLoading: boolean;
}) {
    return (
        <TextField
            placeholder={texts.lastNamePlaceholder}
            value={lastName}
            blurOnSubmit={false}
            autoCorrect={false}
            autoCapitalize={'words'}
            keyboardType={'default'}
            returnKeyType="next"
            editable={!isLoading}
            onChangeText={setLastName}
        />
    );
}

function EmailInput({
    email,
    setEmail,
    isLoading,
}: {
    email: string;
    setEmail: (email: string) => void;
    isLoading: boolean;
}) {
    return (
        <TextField
            value={email}
            placeholder={texts.emailPlaceholder}
            autoCorrect={false}
            autoCapitalize={'none'}
            keyboardType={'email-address'}
            returnKeyType="next"
            editable={!isLoading}
            onChangeText={setEmail}
        />
    );
}

function PasswordInput({
    password,
    setPassword,
    isLoading,
    placeholder,
    hasError,
    errorMessage,
}: {
    password: string;
    setPassword: (password: string) => void;
    isLoading: boolean;
    placeholder?: string;
    hasError?: boolean;
    errorMessage?: string;
}) {
    const [hidePassword, setHidePassword] = React.useState<boolean>(true);
    return (
        <TextField
            placeholder={placeholder || texts.passwordPlaceholder}
            autoCorrect={false}
            autoCapitalize={'none'}
            secureTextEntry={hidePassword}
            returnKeyType="go"
            editable={!isLoading}
            onChangeText={setPassword}
            {...{ hasError, errorMessage }}>
            <TouchableOpacity onPress={() => setHidePassword(!hidePassword)} activeOpacity={0.8}>
                {hidePassword ? (
                    <Image source={eyeIcon} style={styles.imageEyeIcon} />
                ) : (
                    <Image source={eyeSlashIcon} style={styles.imageEyeIcon} />
                )}
            </TouchableOpacity>
        </TextField>
    );
}

export function TermsOfServiceConsentCheckBox({
    isChecked,
    setIsChecked,
}: {
    isChecked: boolean;
    setIsChecked: (value: boolean) => void;
}) {
    const viewTermsOfService = () => window.open(termsOfServiceLink);
    const viewPrivacyPolicy = () => window.open(privacyPolicyLink);
    return (
        <CheckBox {...{ isChecked, setIsChecked }}>
            <HighlightedText
                style={styles.textCheckBox}
                highlightedStyle={styles.textCheckBoxHighlighted}
                onPressHighlightedTexts={[viewTermsOfService, viewPrivacyPolicy]}>
                {texts.termsOfService}
            </HighlightedText>
        </CheckBox>
    );
}

function CheckBox({
    children,
    isChecked,
    setIsChecked,
}: {
    children: JSX.Element;
    isChecked: boolean;
    setIsChecked: (value: boolean) => void;
}) {
    return (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <TouchableOpacity
                onPress={() => setIsChecked(!isChecked)}
                activeOpacity={0.7}
                style={[
                    styles.containerCheckMark,
                    isChecked ? styles.containerCheckMarkSelected : styles.containerCheckMarkUnselected,
                ]}>
                {isChecked ? (
                    <Image source={checkMarkIcon} style={{ height: 11, width: 13, resizeMode: 'contain' }} />
                ) : null}
            </TouchableOpacity>
            {children}
        </View>
    );
}

export function ErrorMessage({ error }: { error: SignUpError }) {
    let message: string = texts.error.default;
    return (
        <View style={styles.containerErrorMessage}>
            <Text style={styles.textErrorMessage}>{message}</Text>
        </View>
    );
}

export function EmailVerificationMessage({ email, password }: { email: string; password: string }) {
    const size = useWindowSize();
    useSignInAttempt({ email, password });
    return (
        <View style={[styles.containerEmailVerification, { height: size.height }]}>
            <PageLogo />
            <View style={styles.containerEmailVerificationMessage}>
                <Image source={hourglassIllustration} style={styles.imageEmailVerificationIllustration} />
                <Text style={styles.textEmailVerificationTitle}>{texts.emailVerification.title}</Text>
                <HighlightedText
                    style={styles.textEmailVerificationSubtitle}
                    highlightedStyle={styles.textEmailVerificationSubtitleHighlighted}>
                    {texts.emailVerification.subtitle({ email })}
                </HighlightedText>
            </View>
        </View>
    );
}

// The Joko logo sits in the top-left corner of the page on the screens that have no card, as in
// the design (Figma nodes 4965:9310 and 5132:13479).
export function PageLogo() {
    return (
        <View style={styles.containerPageLogo}>
            <Image source={logo} style={styles.imagePageLogo} />
        </View>
    );
}

const EMAIL_VERIFICATION_POLLING_INTERVAL_IN_MS = 5000;

// Signs the user in as soon as they confirm their address, so the screen moves on by itself: there
// is no action to offer them here (Cognito refuses to authenticate an unconfirmed account). It polls
// on a timer and whenever the tab regains focus, and must stay invisible — swapping the card for a
// loading animation made a stray animation flash on every tab switch (QA CUX-800).
function useSignInAttempt({ email, password }: { email: string; password: string }) {
    const signIn = React.useCallback(async () => {
        if (!email || !password) return;
        try {
            await Auth.signIn({ username: email, password });
            clearPendingEmailVerification();
            window.location.reload();
        } catch (error) {}
    }, [email, password]);
    React.useEffect(() => {
        const intervalId = window.setInterval(signIn, EMAIL_VERIFICATION_POLLING_INTERVAL_IN_MS);
        window.addEventListener('visibilitychange', signIn);
        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('visibilitychange', signIn);
        };
    }, [signIn]);
}

const styles = StyleSheet.create({
    containerField: {
        marginBottom: 16,
    },
    containerPasswordRequirements: {
        marginTop: 8,
    },
    // Design-system checkbox: squared outline (radius xxSmall), Joko Green box with a white tick
    // when selected (`background.positive` + `content.primary`, as in `user-mobile-app`
    // src/designSystem/components/dataEntry/BaseCheckboxItem.tsx), `border.secondary` (neutral 100)
    // when empty — the same border as the text fields.
    containerCheckMark: {
        justifyContent: 'center',
        alignItems: 'center',
        height: 24,
        width: 24,
        borderRadius: 4,
    },
    containerCheckMarkSelected: {
        backgroundColor: color.jokoGreen,
    },
    containerCheckMarkUnselected: {
        borderWidth: 1,
        borderColor: color.neutralBorder,
        backgroundColor: color.white,
    },
    containerErrorMessage: {
        backgroundColor: color.negativeLight,
        paddingVertical: 8,
        paddingHorizontal: 25,
        borderRadius: 8,
        marginTop: 16,
    },
    containerSignUpButton: {
        marginTop: 32,
    },
    // No card: the content sits directly on the white page, as in the design
    containerEmailVerification: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: color.white,
    },
    containerEmailVerificationMessage: {
        width: 360,
        alignItems: 'center',
    },
    containerPageLogo: {
        position: 'absolute',
        top: 40,
        left: 48,
    },
    textTitle: {
        fontFamily: font.wallopSansSemiBold,
        fontSize: 22,
        color: color.black,
        marginBottom: 32,
    },
    textCheckBox: {
        fontFamily: font.instrumentSansMedium,
        color: color.tundora,
        fontSize: 14,
        paddingLeft: 15,
    },
    // Link style aligned with the user-web-app (app.joko.com) design system: black, semibold, underlined
    textCheckBoxHighlighted: {
        fontFamily: font.instrumentSansSemiBold,
        color: color.black,
        textDecorationLine: 'underline',
    },
    textErrorMessage: {
        color: color.negative,
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
        alignSelf: 'center',
        textAlign: 'center',
    },
    textSignUpButton: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
    },
    containerSignIn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 24,
    },
    textSignIn: {
        textAlign: 'center',
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
        color: color.black,
    },
    // Link style aligned with the user-web-app (app.joko.com) design system: black, semibold, underlined
    textSignInButton: {
        textAlign: 'center',
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        color: color.black,
        borderBottomWidth: 1,
        borderBottomColor: color.black,
    },
    textEmailVerificationTitle: {
        fontFamily: font.wallopSansSemiBold,
        fontSize: 20,
        color: color.black,
        textAlign: 'center',
        marginBottom: 8,
    },
    textEmailVerificationSubtitle: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
        color: color.osloGray,
        textAlign: 'center',
    },
    textEmailVerificationSubtitleHighlighted: {
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        color: color.osloGray,
    },
    // Design-system eye / eye-slash glyphs at `icons.medium` (24px)
    imageEyeIcon: {
        width: 24,
        height: 24,
        resizeMode: 'contain',
    },
    imagePageLogo: {
        width: 120,
        height: 40,
        resizeMode: 'contain',
    },
    // 80px asset with a 36px gap under it, per the design
    imageEmailVerificationIllustration: {
        width: 80,
        height: 80,
        resizeMode: 'contain',
        marginBottom: 36,
    },
});
